import { downloadQrCard } from "../lib/qrCardGenerator";
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import {
  MessageSquare,
  AlertTriangle,
  Search,
  Forward,
  Eye,
  QrCode,
  Download,
  Loader2,
  X,
  CheckCircle,
  Clock,
  Filter,
  RefreshCw
} from "lucide-react";

export default function FeedbackManagement() {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [filter, setFilter] = useState("all"); // all, suggestion, complaint
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog states
  const [viewItem, setViewItem] = useState(null);
  const [forwardItem, setForwardItem] = useState(null);
  const [forwardTo, setForwardTo] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [forwardLoading, setForwardLoading] = useState(false);
  const [qrDialog, setQrDialog] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/feedback`, { headers }),
        axios.get(`${API}/feedback/stats`, { headers })
      ];
      if (isAdmin) {
        requests.push(axios.get(`${API}/users`, { headers }));
      }
      const results = await Promise.all(requests);
      setFeedbackList(results[0].data);
      setStats(results[1].data);
      if (isAdmin && results[2]) {
        const managerRoles = ["maintenance_manager", "it_manager", "medical_manager", "procurement_manager"];
        setManagers(results[2].data.filter(u => managerRoles.includes(u.role)));
      }
    } catch {
      toast.error("فشل تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleForward = async () => {
    if (!forwardTo) {
      toast.error("اختر المدير المسؤول");
      return;
    }
    setForwardLoading(true);
    try {
      await axios.put(`${API}/feedback/${forwardItem.id}/forward`, {
        forwarded_to: forwardTo,
        admin_notes: adminNotes || null
      }, { headers });
      toast.success("تم إعادة التوجيه بنجاح");
      setForwardItem(null);
      setForwardTo("");
      setAdminNotes("");
      fetchData();
    } catch {
      toast.error("فشل إعادة التوجيه");
    } finally {
      setForwardLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/feedback/${id}/status`, { status: newStatus }, { headers });
      toast.success("تم تحديث الحالة");
      fetchData();
      if (viewItem?.id === id) {
        setViewItem(prev => ({ ...prev, status: newStatus }));
      }
    } catch {
      toast.error("فشل تحديث الحالة");
    }
  };

  const showQrCode = async () => {
    setQrDialog(true);
    setQrLoading(true);
    try {
      const res = await axios.get(`${API}/feedback/qr-code?base_url=${encodeURIComponent(window.location.origin)}`, { headers });
      setQrData(res.data);
    } catch {
      toast.error("فشل توليد QR Code");
    } finally {
      setQrLoading(false);
    }
  };

  const downloadQr = async () => {
    if (!qrData) return;
    await downloadQrCard({
      qrBase64: qrData.qr_code,
      name: "الاقتراحات والشكاوى",
      floor: "",
      building: "",
      bottomTextAr: "امسح الرمز لتقديم اقتراح أو شكوى",
      bottomTextEn: "Scan to submit a suggestion or complaint",
      bgColor: "#655dc6"
    });
  };

  const getStatusBadge = (status) => {
    const map = {
      new: { label: "جديد", bg: "bg-blue-100 text-blue-700" },
      reviewed: { label: "تمت المراجعة", bg: "bg-yellow-100 text-yellow-700" },
      in_progress: { label: "قيد المعالجة", bg: "bg-orange-100 text-orange-700" },
      resolved: { label: "تم الحل", bg: "bg-green-100 text-green-700" },
      closed: { label: "مغلق", bg: "bg-slate-100 text-slate-600" }
    };
    const s = map[status] || map.new;
    return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s.bg}`}>{s.label}</span>;
  };

  const getTypeBadge = (type) => {
    if (type === "suggestion") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <MessageSquare className="w-3 h-3" /> اقتراح
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <AlertTriangle className="w-3 h-3" /> شكوى
      </span>
    );
  };

  const getRoleName = (role) => {
    const map = {
      maintenance_manager: "مدير الصيانة",
      it_manager: "مدير تقنية المعلومات",
      medical_manager: "المدير الطبي",
      procurement_manager: "مدير المشتريات"
    };
    return map[role] || role;
  };

  // Filter logic
  const filtered = feedbackList.filter(fb => {
    if (filter !== "all" && fb.type !== filter) return false;
    if (statusFilter !== "all" && fb.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return fb.reporter_name.toLowerCase().includes(q) ||
             fb.content.toLowerCase().includes(q) ||
             fb.reporter_phone.includes(q);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#4a3fa8]">الاقتراحات والشكاوى</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin ? "إدارة ملاحظات الزوار والموظفين" : "الملاحظات الموجّهة إليك"}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={showQrCode} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2" data-testid="feedback-qr-btn">
            <QrCode className="w-4 h-4" />
            رمز QR للصفحة
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: "الإجمالي", value: stats.total, color: "bg-[#655dc6]" },
            { label: "اقتراحات", value: stats.suggestions, color: "bg-[#1565C0]" },
            { label: "شكاوى", value: stats.complaints, color: "bg-[#C62828]" },
            { label: "جديدة", value: stats.new, color: "bg-blue-500" },
            { label: "قيد المراجعة", value: stats.reviewed, color: "bg-orange-500" },
            { label: "تم الحل", value: stats.resolved, color: "bg-green-600" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm text-center">
              <div className={`w-8 h-8 ${s.color} rounded-lg mx-auto mb-2 flex items-center justify-center`}>
                <span className="text-white text-sm font-bold">{s.value}</span>
              </div>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="بحث بالاسم أو المحتوى أو الرقم..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-10"
            data-testid="feedback-search"
          />
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter} dir="rtl">
            <SelectTrigger className="w-[140px]" data-testid="feedback-type-filter">
              <Filter className="w-3.5 h-3.5 ml-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent dir="rtl">
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="suggestion">اقتراحات</SelectItem>
              <SelectItem value="complaint">شكاوى</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter} dir="rtl">
            <SelectTrigger className="w-[140px]" data-testid="feedback-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent dir="rtl">
              <SelectItem value="all">كل الحالات</SelectItem>
              <SelectItem value="new">جديد</SelectItem>
              <SelectItem value="reviewed">تمت المراجعة</SelectItem>
              <SelectItem value="in_progress">قيد المعالجة</SelectItem>
              <SelectItem value="resolved">تم الحل</SelectItem>
              <SelectItem value="closed">مغلق</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Feedback List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg">لا توجد ملاحظات</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((fb) => (
            <Card key={fb.id} className="border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`feedback-card-${fb.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {getTypeBadge(fb.type)}
                      {getStatusBadge(fb.status)}
                      {fb.forwarded_to_name && (
                        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                          → {fb.forwarded_to_name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-2 mb-2">{fb.content}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                      <span>{fb.reporter_name}</span>
                      <span dir="ltr">{fb.reporter_phone}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(fb.created_at).toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric" })}
                      </span>
                      {fb.images?.length > 0 && (
                        <span className="text-[#655dc6]">{fb.images.length} صورة</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setViewItem(fb)} className="gap-1" data-testid={`view-feedback-${fb.id}`}>
                      <Eye className="w-3.5 h-3.5" /> عرض
                    </Button>
                    {isAdmin && (
                      <Button variant="outline" size="sm" onClick={() => { setForwardItem(fb); setAdminNotes(""); setForwardTo(""); }} className="gap-1 text-[#655dc6]" data-testid={`forward-feedback-${fb.id}`}>
                        <Forward className="w-3.5 h-3.5" /> توجيه
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewItem && getTypeBadge(viewItem.type)}
              {viewItem && getStatusBadge(viewItem.status)}
            </DialogTitle>
          </DialogHeader>
          {viewItem && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{viewItem.content}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-400">الاسم:</span>
                  <p className="font-medium">{viewItem.reporter_name}</p>
                </div>
                <div>
                  <span className="text-slate-400">الجوال:</span>
                  <p className="font-medium" dir="ltr">{viewItem.reporter_phone}</p>
                </div>
                <div>
                  <span className="text-slate-400">التاريخ:</span>
                  <p className="font-medium">{new Date(viewItem.created_at).toLocaleString("ar-SA")}</p>
                </div>
                {viewItem.forwarded_to_name && (
                  <div>
                    <span className="text-slate-400">موجه إلى:</span>
                    <p className="font-medium">{viewItem.forwarded_to_name}</p>
                  </div>
                )}
              </div>

              {viewItem.admin_notes && (
                <div>
                  <span className="text-xs text-slate-400">ملاحظات الإدارة:</span>
                  <p className="text-sm bg-yellow-50 p-2 rounded mt-1">{viewItem.admin_notes}</p>
                </div>
              )}

              {viewItem.images?.length > 0 && (
                <div>
                  <span className="text-xs text-slate-400 mb-2 block">الصور المرفقة:</span>
                  <div className="flex flex-wrap gap-2">
                    {viewItem.images.map((img, i) => (
                      <img key={i} src={img} alt="" className="w-24 h-24 object-cover rounded-lg border cursor-pointer hover:opacity-80"
                        onClick={() => setPreviewImage(img)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Status Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {viewItem.status === "new" && (
                  <Button size="sm" variant="outline" onClick={() => handleStatusChange(viewItem.id, "reviewed")} data-testid="mark-reviewed">
                    تمت المراجعة
                  </Button>
                )}
                {["new", "reviewed", "in_progress"].includes(viewItem.status) && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleStatusChange(viewItem.id, "resolved")} data-testid="mark-resolved">
                    <CheckCircle className="w-3.5 h-3.5 ml-1" /> تم الحل
                  </Button>
                )}
                {viewItem.status !== "closed" && (
                  <Button size="sm" variant="outline" className="text-slate-500" onClick={() => handleStatusChange(viewItem.id, "closed")} data-testid="mark-closed">
                    إغلاق
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Forward Dialog */}
      <Dialog open={!!forwardItem} onOpenChange={() => setForwardItem(null)}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>إعادة توجيه لمدير</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">اختر المدير المسؤول</label>
              <Select value={forwardTo} onValueChange={setForwardTo} dir="rtl">
                <SelectTrigger data-testid="forward-select">
                  <SelectValue placeholder="اختر المدير..." />
                </SelectTrigger>
                <SelectContent dir="rtl">
                  {managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.full_name} ({getRoleName(m.role)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">ملاحظات (اختياري)</label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="أضف ملاحظة للمدير..."
                rows={3}
                data-testid="forward-notes"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setForwardItem(null)}>إلغاء</Button>
            <Button onClick={handleForward} disabled={forwardLoading} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1" data-testid="forward-confirm-btn">
              {forwardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Forward className="w-4 h-4" />}
              توجيه
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialog} onOpenChange={setQrDialog}>
        <DialogContent className="max-w-sm text-center" dir="rtl">
          <DialogHeader>
            <DialogTitle>رمز QR - الاقتراحات والشكاوى</DialogTitle>
          </DialogHeader>
          {qrLoading ? (
            <div className="py-10 flex justify-center">
              <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
            </div>
          ) : qrData ? (
            <div className="space-y-4">
              <img src={`data:image/png;base64,${qrData.qr_code}`} alt="QR Code" className="w-48 h-48 mx-auto" />
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-1">رابط الصفحة الحالي:</p>
                <p className="text-sm font-medium text-[#4a3fa8] break-all" dir="ltr">{qrData.url}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={downloadQr} className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2" data-testid="download-qr-btn">
                  <Download className="w-4 h-4" /> تحميل
                </Button>
                <Button onClick={showQrCode} variant="outline" className="flex-1 gap-2" data-testid="regenerate-feedback-qr-btn">
                  <RefreshCw className="w-4 h-4" /> إعادة توليد
                </Button>
              </div>
              <p className="text-xs text-slate-400">رمز QR يُولّد من رابط الموقع الحالي. بعد Deploy على دومين جديد، اضغط "إعادة توليد" للتحديث.</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-3xl p-2" dir="rtl">
          <img src={previewImage} alt="" className="w-full h-auto rounded-lg" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
